import '../css/PanelGroupingOptions.less';

import * as React from 'react';

import classNames from 'classnames';
import {Button, Checkbox} from 'semantic-ui-react';
import LabeledOption from './elements/LabeledOption';
import {RunsLinePlotConfig} from './PanelRunsLinePlot';
import {BarChartConfig} from './PanelBarChart';
import {ScalarChartConfig} from './PanelScalarChart';
import docUrl from '../util/doc_urls';
import ProjectFieldSelector from './ProjectFieldSelector';
import ModifiedDropdown from './elements/ModifiedDropdown';

import * as Query from '../util/queryts';
import makeComp from '../util/profiler';

type PanelConfig = BarChartConfig | RunsLinePlotConfig | ScalarChartConfig;
interface PanelGroupingOptions {
  type: 'lines' | 'bars' | 'scalar';
  pageQuery: Query.Query;
  config: PanelConfig;
  updateConfig: (newConfig: PanelConfig) => void;
  usingExpressions: boolean;
  isGrouped: boolean; // is grouping happening (could be happening from outside of panel)
  disabled?: boolean;
  totalRuns: number;
  defaultMaxGroupRuns: number;
  absoluteMaxGroupRuns: number;
  singleRun?: boolean;
}

const renderPlotType = (
  config: BarChartConfig,
  updateConfig: (newConfig: PanelConfig) => void,
  usingExpressions: boolean
) => {
  return (
    <LabeledOption
      label="Style"
      helpText="Presentation mode for grouped data"
      docUrl={docUrl.compareMetrics + '#grouping'}
      option={
        <Button.Group size="tiny">
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active':
                (config as BarChartConfig).plotStyle == null ||
                (config as BarChartConfig).plotStyle === 'bar',
            })}
            onClick={() => {
              updateConfig({plotStyle: 'bar'});
            }}>
            Bar
          </Button>
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active':
                (config as BarChartConfig).plotStyle === 'boxplot',
            })}
            onClick={() => {
              updateConfig({plotStyle: 'boxplot'});
            }}>
            Box Plot
          </Button>
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active':
                (config as BarChartConfig).plotStyle === 'violin',
            })}
            onClick={() => {
              updateConfig({plotStyle: 'violin'});
            }}>
            Violin
          </Button>
        </Button.Group>
      }
    />
  );
};

export const renderAggRange = (
  config: PanelConfig,
  updateConfig: (newConfig: PanelConfig) => void,
  usingExpressions: boolean,
  includeSampleOption: boolean = true
) => {
  return (
    <LabeledOption
      label="Range"
      helpText="The aggregation calculation for the range that displays for a group."
      docUrl={docUrl.compareMetrics + '#grouping'}
      option={
        <Button.Group size="tiny">
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active': config.groupArea === 'none',
            })}
            onClick={() => {
              updateConfig({groupArea: 'none'});
            }}>
            None
          </Button>
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active':
                config.groupArea == null || config.groupArea === 'minmax',
            })}
            onClick={() => {
              updateConfig({groupArea: 'minmax'});
            }}>
            Min/Max
          </Button>
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active': config.groupArea === 'stddev',
            })}
            onClick={() => {
              updateConfig({groupArea: 'stddev'});
            }}>
            Std Dev
          </Button>
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active': config.groupArea === 'stderr',
            })}
            onClick={() => {
              updateConfig({groupArea: 'stderr'});
            }}>
            Std Err
          </Button>
          {includeSampleOption && (
            <Button
              size="small"
              disabled={usingExpressions}
              className={classNames({
                'action-button--active': config.groupArea === 'samples',
              })}
              onClick={() => {
                updateConfig({groupArea: 'samples'});
              }}>
              Samples
            </Button>
          )}
        </Button.Group>
      }
    />
  );
};

export function renderAggType(
  config: PanelConfig,
  updateConfig: (newConfig: PanelConfig) => void,
  defaultGroupAgg: 'mean' | 'median' | 'max' | 'min' = 'mean',
  usingExpressions: boolean = false
) {
  return (
    <LabeledOption
      label="Agg"
      helpText="The aggregation calculation for the line that displays for a group."
      docUrl={docUrl.compareMetrics + '#grouping'}
      option={
        <Button.Group size="tiny">
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active':
                (config.groupAgg == null && defaultGroupAgg === 'mean') ||
                config.groupAgg === 'mean',
            })}
            onClick={() => {
              updateConfig({groupAgg: 'mean'});
            }}>
            Mean
          </Button>
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active':
                (config.groupAgg == null && defaultGroupAgg === 'median') ||
                config.groupAgg === 'median',
            })}
            onClick={() => {
              updateConfig({groupAgg: 'median'});
            }}>
            Median
          </Button>
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active':
                (config.groupAgg == null && defaultGroupAgg === 'min') ||
                config.groupAgg === 'min',
            })}
            onClick={() => {
              updateConfig({groupAgg: 'min'});
            }}>
            Min
          </Button>
          <Button
            size="small"
            disabled={usingExpressions}
            className={classNames({
              'action-button--active':
                (config.groupAgg == null && defaultGroupAgg === 'max') ||
                config.groupAgg === 'max',
            })}
            onClick={() => {
              updateConfig({groupAgg: 'max'});
            }}>
            Max
          </Button>
        </Button.Group>
      }
    />
  );
}

export function multiRunAggOptions(props: PanelGroupingOptions) {
  // show user options to aggregate over runs
  const {config, updateConfig, pageQuery, disabled} = props;
  return (
    <>
      <LabeledOption
        label="Runs"
        helpText="Aggregate over runs"
        docUrl={docUrl.compareMetrics + '#grouping'}
        option={
          <>
            <Checkbox
              toggle
              disabled={disabled}
              checked={config.aggregate}
              name="aggregate"
              onChange={(e, value) =>
                updateConfig({
                  aggregate: value.checked,
                })
              }
            />
            {config.aggregate && (
              <>
                <span>Group by</span>
                <ProjectFieldSelector
                  className={'groupby-picker'}
                  disabled={!props.config.aggregate}
                  query={pageQuery}
                  selection
                  columns={['config']}
                  defaultKeys={['config:None']}
                  value={`config:${config.groupBy ?? 'None'}`}
                  multi={false}
                  searchByKeyAndText
                  setValue={value =>
                    updateConfig({
                      groupBy: value.split(':')[1],
                    })
                  }
                />
              </>
            )}
          </>
        }
      />
    </>
  );
}

export function singleRunAggOptions(props: PanelGroupingOptions) {
  // user will always aggregate over metrics
  // if that's not possible show warning
  const {config} = props;
  return (
    <>
      {(config.metrics == null || config.metrics.length <= 1) && (
        <>
          <p>Select multiple metrics to enable grouping</p>
        </>
      )}
    </>
  );
}

const PanelGroupingOptions = makeComp(
  (props: PanelGroupingOptions) => {
    const {
      config,
      updateConfig,
      singleRun,
      disabled,
      usingExpressions,
      isGrouped,
      totalRuns,
      defaultMaxGroupRuns,
      absoluteMaxGroupRuns,
    } = props;

    const showAggregationOptions =
      (props.type === 'bars' &&
        (config as BarChartConfig).plotStyle !== 'boxplot' &&
        (config as BarChartConfig).plotStyle !== 'violin') ||
      props.type === 'lines';

    const disableAggregationOptions =
      props.type === 'lines' && usingExpressions;

    return (
      <>
        {singleRun ? singleRunAggOptions(props) : multiRunAggOptions(props)}
        {(config.aggregateMetrics === true ||
          ((props.type === 'bars' || props.type === 'lines') &&
            config.metrics != null &&
            config.metrics.length > 1)) && (
          <LabeledOption
            label="Metrics"
            helpText="Aggregate over all metrics"
            option={
              <Checkbox
                toggle
                disabled={disabled}
                checked={config.aggregateMetrics}
                name="aggregate"
                onChange={(e, value) =>
                  updateConfig({
                    aggregateMetrics: value.checked,
                  })
                }
              />
            }
          />
        )}
        {isGrouped && (
          <>
            <div className="group-options">
              <div className="group-option">
                {usingExpressions && (
                  <div className="hint-text">
                    <p>
                      Grouping functions are disabled while using expressions.
                      In order to reenable, remove all expressions.
                    </p>
                  </div>
                )}
                {props.type === 'bars' &&
                  renderPlotType(
                    config as BarChartConfig,
                    updateConfig,
                    usingExpressions
                  )}
                {showAggregationOptions &&
                  renderAggType(
                    config,
                    updateConfig,
                    'mean',
                    disableAggregationOptions
                  )}
                {showAggregationOptions &&
                  renderAggRange(
                    config,
                    updateConfig,
                    disableAggregationOptions,
                    props.type === 'lines'
                  )}
              </div>
            </div>
          </>
        )}
        {isGrouped && totalRuns >= defaultMaxGroupRuns && (
          <LabeledOption
            label="Sampled Runs"
            helpText="Number of runs to sample from for grouping (large numbers may impact performance)."
            docUrl={docUrl.compareMetrics + '#grouping'}
            option={
              <ModifiedDropdown
                lazyLoad
                className="no-wrap-options"
                value={config.groupRunsLimit || defaultMaxGroupRuns}
                style={{minWidth: 60}}
                selection
                options={[
                  defaultMaxGroupRuns,
                  // limit the absolute maximum number of runs to group so we don't hit performance issues
                  Math.min(totalRuns, absoluteMaxGroupRuns),
                ]
                  .sort()
                  .map(n => ({
                    value: n,
                    text: `${n}`,
                  }))}
                onChange={(e, {value}) => {
                  if (typeof value === 'number') {
                    updateConfig({
                      groupRunsLimit: value,
                    });
                  }
                }}
              />
            }
          />
        )}
      </>
    );
  },
  {id: 'PanelGroupingOptions'}
);

export default PanelGroupingOptions;
